<template>
  <nav>
    <fill-area :offset="isLoggedIn ? 100 : 0" :disabled="!$vuetify.display.mobile">
      <v-list-item v-if="isLoggedIn && $vuetify.display.mobile" class="mt-4">
        <user-profile-link :user-id="user?.id">
          <div class="d-flex align-center gap-1">
            <user-avatar :user="user" :size="56" />
            <div>
              <div class="font-size-title-small">{{ user.display_name }}</div>
              <div class="font-weight-normal font-size-medium">{{ user.email }}</div>
            </div>
          </div>
        </user-profile-link>
      </v-list-item>
      <nav-item
        v-for="navigationItem in displayedNavigationItems"
        :key="navigationItem.title"
        :class="{ 'color-post-text': $vuetify.display.mobile }"
        class="mt-1"
        :navigation-item="navigationItem"
        @click="registerPaywallSourceIfPaywallLinkClicked(navigationItem)"
      />
      <the-button
        v-if="folded && maxItemsBeforeFold && navigationItems.length > maxItemsBeforeFold"
        text
        block
        custom-colors
        class="see-more mt-2 color-wall-text"
        @click="folded = !folded"
      >
        <div class="d-flex align-center gap-2 font-weight-normal">
          <i-custom-arrow-down-thin />
          <span>{{ $translate("common.seeMore") }}</span>
        </div>
      </the-button>
    </fill-area>
    <v-list-item v-if="isLoggedIn && $vuetify.display.mobile && configuration.invitationLinkOnMoreMenu">
      <invite-members-button button-secondary :analytics-key="analyticsKey" @open="registerOpenInviteButtonEvent" />
    </v-list-item>
  </nav>
</template>

<script>
import { mapState } from "pinia"

import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"
import { useConfigurationStore } from "@/store/configuration.js"

import InviteMembersButton from "@/components/InviteMembersButton.vue"
import NavItem from "@/components/navs/NavItem.vue"

import { registerAnalyticsEvent } from "@/services/analytics.js"
import * as paywallService from "@/services/paywallService.js"
import * as texts from "@/services/texts.js"

import { paywallSourceTypes } from "@/constants/paywall.js"
import * as urls from "@/services/urls.js"

//There shouldn't be new types anymore as those should go under 'deeplink' type
//If there is a new type - make sure it is really needed to be different
const supportedLinkTypes = {
  ACTIVITY_FEED: "onefeed",
  WEB: "web",
  EVENTS: "events",
  GROUP: "wall",
  FOLDER: "archive",
  LESSON: "lesson",
  DEEPLINK: "deeplink"
}

export default {
  components: {
    NavItem,
    InviteMembersButton
  },
  props: {
    maxItemsBeforeFold: {
      type: Number
    }
  },
  data: () => ({
    analyticsKey: texts.generateUUID(),
    folded: true
  }),
  computed: {
    ...mapState(useConfigurationStore, ["configuration"]),
    ...mapState(useUserSessionStore, ["isLoggedIn", "user"]),
    ...mapState(useGroupsStore, ["groupForKey"]),
    validNavigation() {
      return (this.configuration.navigation || []).filter((item) => {
        if (item.type === supportedLinkTypes.GROUP && item.wall_id && !this.groupForKey(item.wall_id)) {
          return false
        }
        if (item.type === supportedLinkTypes.DEEPLINK) {
          const route = urls.getRouterRouteFromDeepLink(item.link)
          if (route?.name === this.$routes.GROUPS && route.params?.groupId && !this.groupForKey(route.params.groupId)) {
            return false
          }
        }
        return true
      })
    },
    navigationItems() {
      const mapped = this.validNavigation
        .map((item) => ({
          title: item.title,
          external: item.type === supportedLinkTypes.WEB,
          link: this.getLink(item)
        }))
        .filter((item) => item.link)

      if (!this.$vuetify.display.mobile) {
        mapped.push({
          title: this.$translate("common.settings"),
          link: { name: this.$routes.PROFILE_SETTINGS }
        })
      }

      return mapped
    },
    displayedNavigationItems() {
      if (!this.maxItemsBeforeFold) {
        return this.navigationItems
      }
      return this.folded ? this.navigationItems.slice(0, this.maxItemsBeforeFold) : this.navigationItems
    }
  },
  methods: {
    registerOpenInviteButtonEvent() {
      registerAnalyticsEvent({
        name: "invite-link-more-menu-item-open",
        attributes: { key: this.analyticsKey }
      })
    },
    registerPaywallSourceIfPaywallLinkClicked(navigationItem) {
      if (navigationItem.link.name === this.$routes.PAYWALL) {
        paywallService.registerLastSource({
          sourceId: navigationItem.title,
          sourceType: paywallSourceTypes.CUSTOM_NAVIGATION
        })
      }
    },
    getLink(navigationItem) {
      switch (navigationItem.type) {
        case supportedLinkTypes.DEEPLINK:
          return urls.getRouterRouteFromDeepLink(navigationItem.link)
        case supportedLinkTypes.ACTIVITY_FEED:
          return "/groups"
        case supportedLinkTypes.WEB:
          return navigationItem.url
        case supportedLinkTypes.EVENTS:
          return "/events"
        case supportedLinkTypes.GROUP:
          return `/groups/${navigationItem.wall_id}`
        case supportedLinkTypes.FOLDER:
          return `/folders/${navigationItem.folder_id}`
        default:
          return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.see-more {
  :deep(.v-btn__content) {
    justify-content: flex-start;
  }
}
</style>
